import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Filter } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: "bg-[#F2B705]/15 text-[#F2B705] border-[#F2B705]/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  quoted: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  booked: "bg-green-500/15 text-green-400 border-green-500/30",
  lost: "bg-red-500/15 text-red-400 border-red-500/30",
};
const STATUSES = ["new", "contacted", "quoted", "booked", "lost"];

export default function LeadsInbox({ token }) {
  const [leads, setLeads] = useState(null);
  const [sources, setSources] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const loadLeads = (s = statusFilter, src = sourceFilter) => {
    const p = new URLSearchParams();
    if (s) p.set("status", s);
    if (src) p.set("source", src);
    return axios.get(`${API}/leads?${p}`, H).then((r) => setLeads(r.data));
  };

  useEffect(() => {
    axios.get(`${API}/leads/intake-config`, H).then((r) => setSources(r.data.sources || [])).catch(() => {});
    loadLeads();
    // eslint-disable-next-line
  }, [token]);

  const setLeadStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, H);
      toast.success(`Lead marked ${status}.`);
      loadLeads();
    } catch { toast.error("Could not update lead."); }
  };

  if (!leads) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading leads…</div>;

  return (
    <div data-testid="leads-inbox" className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-display font-bold text-white mr-auto">Leads Inbox <span className="text-white/40 font-normal text-sm">({leads.length})</span></h3>
        <Filter size={14} className="text-white/40" />
        <select data-testid="lead-status-filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); loadLeads(e.target.value, sourceFilter); }} className="bg-black border border-white/15 rounded-md px-3 py-1.5 text-sm text-white/80">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="lead-source-filter" value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); loadLeads(statusFilter, e.target.value); }} className="bg-black border border-white/15 rounded-md px-3 py-1.5 text-sm text-white/80">
          <option value="">All sources</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 text-xs uppercase tracking-wider text-left border-b border-white/10">
              <th className="py-2 pr-3">Lead</th>
              <th className="py-2 pr-3">Source</th>
              <th className="py-2 pr-3">Details</th>
              <th className="py-2 pr-3">Received</th>
              <th className="py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-white/5 align-top">
                <td className="py-2.5 pr-3">
                  <div className="text-white font-semibold">{l.name}</div>
                  <div className="text-white/45 text-xs">{[l.email, l.phone].filter(Boolean).join(" · ") || "no contact info"}</div>
                </td>
                <td className="py-2.5 pr-3"><span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs whitespace-nowrap">{l.source}</span></td>
                <td className="py-2.5 pr-3 text-white/60 text-xs max-w-[280px]">
                  {l.vehicle && <div>🚗 {l.vehicle}</div>}
                  {l.call_summary && <div className="text-[#F2B705]/80">☎ {l.call_summary}{l.call_duration ? ` (${l.call_duration})` : ""}</div>}
                  {l.message && <div className="truncate">{l.message}</div>}
                </td>
                <td className="py-2.5 pr-3 text-white/50 text-xs whitespace-nowrap">{fmtDateTime(l.created_at)}</td>
                <td className="py-2.5">
                  <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)}
                    className={`border rounded-full px-2.5 py-1 text-xs font-semibold bg-transparent ${STATUS_COLORS[l.status] || "border-white/15 text-white/70"}`}>
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-black text-white">{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-white/40">No leads match this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
